import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import language from '../../variables/language';
import IconAdd from '../../components/icons/IconAdd';
import IconDelete from '../../components/icons/IconDelete';
import IconBuild from '../../components/icons/IconBuild';
import IconInfo from '../../components/icons/IconInfo';
import CourseSearchSelect from './CourseSearchSelect';

// No props, self-fetching via useParams - same convention as
// StudentWords.jsx, the tab this one sits right after.
const StudentCourses = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const lang = language[language.lang];

   const [studentCourses, setStudentCourses] = useState([]);
   const [status, setStatus] = useState('loading');
   const [refresh, setRefresh] = useState(0);
   const [pickedCourseId, setPickedCourseId] = useState('');
   const [linking, setLinking] = useState(false);
   const [copying, setCopying] = useState(false);
   const [pendingCounts, setPendingCounts] = useState({});

   const refreshList = () => setRefresh(prev => prev + 1);

   useEffect(() => {
      if(!id) {
         return;
      }
      setStatus('loading');
      axios.get('/api/studentcourses?student=' + id, { withCredentials: true })
         .then(response => {
            setStudentCourses(Array.isArray(response.data) ? response.data : []);
            setStatus('done');
         })
         .catch(err => {
            if(err.response && err.response.status === 401) navigate('/login');
            setStatus('done');
         });
   }, [id, refresh, navigate]);

   // Icon+count badge on any course with pending blur-reveal (or future
   // TeacherApprovalRequest kinds) requests - server resolves each pending
   // request's course id itself (Topic has no direct course FK in general,
   // it's one of 5 possible nullable parent levels), so this is a flat map,
   // no hierarchy-walking needed here.
   useEffect(() => {
      if(!id) {
         return;
      }
      axios.get('/api/teacher-approval-requests/counts-by-course', { params: { student: id }, withCredentials: true })
         .then(response => {
            const map = {};
            (Array.isArray(response.data) ? response.data : []).forEach(row => { map[row.courseId] = row.count; });
            setPendingCounts(map);
         })
         .catch(() => setPendingCounts({}));
   }, [id, refresh]);

   const busy = linking || copying;

   const handleAddLink = () => {
      if(!pickedCourseId || busy) {
         return;
      }
      setLinking(true);
      axios.post('/api/studentcourses', JSON.stringify({ student: id, course: pickedCourseId }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setPickedCourseId('');
         refreshList();
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setLinking(false));
   }

   // Backend performs a synchronous deep-copy of the whole course tree, so
   // this can take longer than a plain link - kept as its own disabled/busy
   // state rather than sharing `linking`'s so the two buttons' spinners
   // don't cross-trigger each other.
   const handleAddCopy = () => {
      if(!pickedCourseId || busy) {
         return;
      }
      setCopying(true);
      axios.post('/api/studentcourses/copy', JSON.stringify({ student: id, sourceCourse: pickedCourseId }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setPickedCourseId('');
         refreshList();
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setCopying(false));
   }

   const handleDelete = (e, studentCourseId) => {
      e.stopPropagation();
      if(!window.confirm('Czy na pewno chcesz usunąć ten element?')) return;
      axios.delete('/api/studentcourses/' + studentCourseId, { withCredentials: true })
         .then(() => refreshList())
         .catch(err => console.log(err));
   }

   // Clicking the row now opens the activity workspace (what the student
   // actually did in this course) - the plain course constructor is still
   // one click away via the small IconBuild button in the row (below),
   // which keeps its own separate returnTo convention (same one
   // exercise-templates/Builder.jsx uses) so its own back button still
   // lands back on this student's card.
   const openWorkspace = (sc) => {
      if(sc.course && sc.course.id) navigate('/students/' + id + '/workspace/' + sc.course.id);
   }
   const openConstructor = (e, sc) => {
      e.stopPropagation();
      if(sc.course && sc.course.id) navigate('/courses/' + sc.course.id, { state: { returnTo: '/students/' + id } });
   }

   const excludeIds = studentCourses.map(sc => sc.course && sc.course.id).filter(Boolean);

   return (
      <div className='student-lessons'>
         <div className='student-lessons-toolbar'>
            <div className='words-tab-add-existing'>
               <CourseSearchSelect value={pickedCourseId} onChange={setPickedCourseId}
                  excludeIds={excludeIds} placeholder={lang.choosePlaceholderCourse} />
               <div className={'student-lessons-add-btn' + (!pickedCourseId || busy ? ' conv-panel-add-btn-disabled' : '')}
                  onClick={handleAddLink} title={lang.addLinkCourse}>
                  <IconAdd />
               </div>
            </div>
            <button type='button' className='student-courses-copy-btn'
               disabled={!pickedCourseId || busy}
               onClick={handleAddCopy}>
               {copying ? lang['Loading ...'] : lang.addCopyCourse}
            </button>
         </div>

         <div className='student-lessons-table-wrap'>
            <table className='student-lessons-table'>
               <thead>
                  <tr>
                     <th>{lang.name}</th>
                     <th></th>
                     <th></th>
                  </tr>
               </thead>
               <tbody>
                  {status === 'loading' ? (
                     <tr className='student-lessons-empty-row'><td colSpan={3}>{lang['Loading ...']}</td></tr>
                  ) : ''}
                  {status === 'done' ? studentCourses.map(sc => (
                     <tr key={sc.id} className='student-lessons-row' onClick={() => openWorkspace(sc)}>
                        <td className='student-lessons-text-cell'>
                           {sc.course ? sc.course.name : ''}
                           {sc.course && pendingCounts[sc.course.id] > 0 ? (
                              <span className='student-courses-pending-badge' title={lang.pendingApprovalHint}>
                                 <IconInfo style={{ width: 12, height: 12 }} />
                                 {pendingCounts[sc.course.id]}
                              </span>
                           ) : ''}
                        </td>
                        <td>
                           <span className={'student-courses-badge' + (sc.sourceCourse ? ' copy' : ' link')}>
                              {sc.sourceCourse ? lang.copyBadge : lang.linkBadge}
                           </span>
                        </td>
                        <td className='student-lessons-actions-cell' onClick={e => e.stopPropagation()}>
                           <div className='student-lessons-icon-btn' onClick={(e) => openConstructor(e, sc)} title='Відкрити в конструкторі'>
                              <IconBuild style={{fill:'#8fc9c3', width:14, height:14, margin:0}} />
                           </div>
                           <div className='student-lessons-icon-btn' onClick={(e) => handleDelete(e, sc.id)}>
                              <IconDelete style={{fill:'#e0876c', width:14, height:14, margin:0}} />
                           </div>
                        </td>
                     </tr>
                  )) : ''}
                  {status === 'done' && studentCourses.length === 0 ? (
                     <tr className='student-lessons-empty-row'><td colSpan={3}>{lang.noCourses}</td></tr>
                  ) : ''}
               </tbody>
            </table>
         </div>
      </div>
   );
}

export default StudentCourses;
